import { EntityKind, NO_MINIMAP_MARK, addMinimapOther, isMinimapPlayer, minimapCreatureKindOf, minimapNpcKindOf, minimapOtherKindOf, type CreatureLook, type EntityState, type MinimapOthers, type NpcLook } from "odyssey-shared";
import { addLiveParty, createLiveParty, partyPlaceOf, resetLiveParty, type PartySeat } from "../party/live-spots.ts";
import type { MinimapOthersReading } from "../report.ts";
import type { CharacterVisitor } from "../world-ui/characters.ts";
import type { MinimapFeed } from "./store.ts";

/**
 * What the feed reads of the page's remote characters: the visit the scene already makes for plates, over the
 * characters drawn this frame.
 */
export interface DrawnOthers {
  forEach(visit: CharacterVisitor, entities: ReadonlyMap<number, EntityState>): void;
}

/**
 * A creature's look in the world's look table, by its appearance, or null past the table's end.
 */
export type CreatureLooks = (appearance: number) => CreatureLook | null;

/**
 * An NPC's look in the world's NPC look table, by its appearance, or null past the table's end.
 */
export type NpcLooks = (appearance: number) => NpcLook | null;

/**
 * The NPC look table of a page whose world places no NPC.
 *
 * @returns Null.
 */
function noNpcLooks(): NpcLook | null {
  return null;
}

/**
 * Fills the minimap's marks of other players, creatures and NPCs each frame from the characters the page draws (P1.9-T179,
 * ADR-061's note of 2026-10-14): the scene's remote table, whose entities are exactly those of the snapshots the page
 * is sent, never anything wider. An entity is marked when `minimapOtherKindOf` says so (a player or creature, alive,
 * not hidden from the viewer by the world view's concealment; a creature's kind from its temperament and rank in the
 * look table, an NPC's from its plate colour in the NPC look table, `minimapNpcKindOf`) and its feet, as drawn, lie inside the vision rectangle
 * (`addMinimapOther`). A player who is a member of the viewer's party (`partyPlaceOf`, by name against the seats the
 * minimap holds) gets no dot: their drawn position goes to the minimap's party buffer instead, alive or dead, hidden or
 * not, as the `partyPositions` feed would place them, so the party's one mark follows them as closely as any other
 * player's dot (P2.12-T35). A player whose name the page does not hold yet keeps their dot and is counted, so a member
 * the feed places inside vision is not marked a second time while such a player is drawn. The visitor is made once, so a frame allocates nothing.
 */
export class OthersFeed {
  private others: ReturnType<MinimapFeed["beginOthers"]> | null = null;
  private selfX = 0;
  private selfY = 0;
  private tileSize = 1;
  private looks: CreatureLooks = () => null;
  private npcLooks: NpcLooks = noNpcLooks;
  private seats: readonly PartySeat[] = [];
  private readonly live = createLiveParty();

  /**
   * Adds the character visited to the buffer when it is to be marked.
   *
   * @param handle Its handle.
   * @param x Its feet across, in world pixels.
   * @param y Its feet down, in world pixels.
   * @param state Its state as the client's view holds it.
   * @param _head Its head height, unused.
   * @param hidden Whether the world view hides it from the viewer.
   */
  private readonly visit: CharacterVisitor = (handle, x, y, state, _head, hidden) => {
    if (state.kind === EntityKind.Player && this.seats.length > 0) {
      const place = partyPlaceOf(state.name, this.seats);
      if (place >= 0) {
        addLiveParty(this.live, place, handle, x / this.tileSize, y / this.tileSize);
        return;
      }
      if (state.name.first === "") {
        this.live.unnamed += 1;
      }
    }
    const look = state.kind === EntityKind.Creature ? this.looks(state.appearance) : null;
    const npc = state.kind === EntityKind.Npc ? this.npcLooks(state.appearance) : null;
    const creature = look !== null ? minimapCreatureKindOf(look.temperament, look.rank) : npc !== null ? minimapNpcKindOf(npc.plate) : NO_MINIMAP_MARK;
    const kind = minimapOtherKindOf(state.kind, state.anim, hidden, creature, state.pvp);
    if (kind >= 0 && this.others !== null) {
      addMinimapOther(this.others, this.selfX, this.selfY, handle, kind, x / this.tileSize, y / this.tileSize, state.name);
    }
  };

  /**
   * Refills the minimap's buffer and the party's drawn positions for this frame.
   *
   * @param minimap The minimap's feed.
   * @param characters The page's remote characters.
   * @param entities The client's view.
   * @param looks The world's look table, for a creature's temperament and rank.
   * @param x The player's feet across, in world pixels, as drawn.
   * @param y The same down.
   * @param tileSize World pixels a tile.
   * @param npcLooks The world's NPC look table, for an NPC's plate colour; none when omitted.
   * @param beside Visits the other maps' figures (ADR-147: the minimap carries on past the seam), each under a key no
   *   handle can be; none when omitted.
   */
  public update(minimap: MinimapFeed, characters: DrawnOthers, entities: ReadonlyMap<number, EntityState>, looks: CreatureLooks, x: number, y: number, tileSize: number, npcLooks: NpcLooks = noNpcLooks, beside?: (visit: CharacterVisitor) => void): void {
    this.looks = looks;
    this.npcLooks = npcLooks;
    this.others = minimap.beginOthers();
    this.selfX = x / tileSize;
    this.selfY = y / tileSize;
    this.tileSize = tileSize;
    this.seats = minimap.partySeats();
    resetLiveParty(this.live);
    characters.forEach(this.visit, entities);
    beside?.(this.visit);
    this.others = null;
    minimap.endOthers();
    minimap.setLiveParty(this.live);
  }
}

/**
 * What the report says of the minimap's marks of other players and creatures: the handles of each kind. Built only when
 * a test reads it, so the frame allocates nothing for it.
 *
 * @param others The buffer.
 * @returns The handles marked as players, those marked as anything else (creatures), and the count dropped past the
 *   capacity.
 */
export function minimapOthersReading(others: MinimapOthers): MinimapOthersReading {
  const players: number[] = [];
  const creatures: number[] = [];
  for (let i = 0; i < others.count; i += 1) {
    (isMinimapPlayer(others.kind[i] as number) ? players : creatures).push(others.handle[i] as number);
  }
  return { players, creatures, dropped: others.dropped };
}
